import { useEffect, useState, useCallback } from 'react';
import { apiGet, apiPost, ApiError } from '../api/client';

export interface AuthUser {
  id: string;
  email: string;
}

export type AnonReason = 'expired';

type State =
  | { kind: 'loading' }
  | { kind: 'anon'; reason?: AnonReason }
  | { kind: 'authed'; user: AuthUser };

// Tracks whether this tab ever saw a successful authed state. Used to distinguish
// "first visit, no cookie" 401 (silent) from "was logged in, session expired" 401
// (show a friendly notice). sessionStorage scope = this tab only, cleared on close.
const EVER_AUTHED_KEY = 'gime5.everAuthed.v1';

// Dev bypass: in Vite dev mode + localhost only, skip the InviteGate/Login/MagicLink
// flow and pretend we're authed as a stub user. Matches the worker's dev guard
// (u_dev_local / dev@local.test) so /api/agent works without a session cookie.
//
// Hostname check is the second line of defense: even if a prod build somehow had
// `import.meta.env.DEV === true`, we still won't enable the stub unless the page
// is loaded from a localhost host. Keeps a misconfigured deploy from leaking the
// stub identity into real users' sessions.
//
// Add ?force_login=1 to the URL to test the real login flow even in dev.
const DEV_STUB_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0']);

function devStubUser(): AuthUser | null {
  if (!import.meta.env.DEV) return null;
  if (typeof window === 'undefined') return null;
  if (!DEV_STUB_HOSTS.has(window.location.hostname)) return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get('force_login') === '1') return null;
  return { id: 'u_dev_local', email: 'dev@local.test' };
}

export function useAuth() {
  const [state, setState] = useState<State>(() => {
    const stub = devStubUser();
    return stub ? { kind: 'authed', user: stub } : { kind: 'loading' };
  });

  const refresh = useCallback(async () => {
    const stub = devStubUser();
    if (stub) {
      setState({ kind: 'authed', user: stub });
      return;
    }
    try {
      const data = await apiGet<{ ok: true; user: AuthUser }>('/api/auth/me');
      sessionStorage.setItem(EVER_AUTHED_KEY, '1');
      setState({ kind: 'authed', user: data.user });
    } catch (err) {
      const wasAuthed = sessionStorage.getItem(EVER_AUTHED_KEY) === '1';
      if (err instanceof ApiError && err.status === 401 && wasAuthed) {
        // Mid-session expiry: previous tab state was authed, cookie now invalid.
        // Clear the marker so re-login starts a fresh "first visit" lifecycle.
        sessionStorage.removeItem(EVER_AUTHED_KEY);
        setState({ kind: 'anon', reason: 'expired' });
      } else {
        // First visit / network failure: silent anon, no banner.
        setState({ kind: 'anon' });
      }
    }
  }, []);

  useEffect(() => {
    if (devStubUser()) return; // already authed via stub, no need to poll /me
    void refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await apiPost('/api/auth/logout', {});
    } finally {
      sessionStorage.removeItem(EVER_AUTHED_KEY);
      setState({ kind: 'anon' });
    }
  }, []);

  return { state, refresh, logout };
}
